import { useState } from "react";
import { Sparkles, TrendingUp, Mail, Type, ClipboardCheck, Image as ImageIcon, Paperclip } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const TOOLS = [
  { key: "sales-insights", label: "Sales Insights", icon: TrendingUp,
    blurb: "What is selling, color/size patterns, three actions for next month.",
    placeholder: "" },
  { key: "campaign-copy", label: "Campaign Copy", icon: Mail,
    blurb: "Emails in the brand voice: abandoned cart, launches, sales.",
    placeholder: "Notes (optional): e.g. 'push the white hoodies'", select: ["abandoned-cart", "launch", "sale", "restock"] },
  { key: "slogans", label: "Slogan Pitches", icon: Type,
    blurb: "New slogan ideas with rationale and layout notes.",
    placeholder: "Theme (optional): e.g. 'libraries', 'winter drop'" },
  { key: "site-review", label: "Site Review", icon: ClipboardCheck,
    blurb: "Ruthless audit of catalog copy: wrong facts, weak jokes, inconsistencies.",
    placeholder: "" },
];

function ToolCard({ tool }) {
  const [out, setOut] = useState("");
  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState("");
  const [kind, setKind] = useState(tool.select ? tool.select[0] : null);
  const Icon = tool.icon;

  const run = async () => {
    setBusy(true);
    setOut("");
    try {
      const body = {};
      if (tool.key === "campaign-copy") { body.kind = kind; body.notes = notes; }
      if (tool.key === "slogans") { body.theme = notes; body.count = 8; }
      const res = await fetch(`${API}/api/admin/ai/${tool.key}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("ut_admin_token")}` },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          const payload = part.slice(6);
          if (payload === "[DONE]") break;
          try { setOut((o) => o + JSON.parse(payload)); } catch { /* partial */ }
        }
      }
    } catch (e) {
      setOut((o) => o + `\n\n[error] ${e.message}`);
    }
    setBusy(false);
  };

  return (
    <div className="border-2 border-black bg-background" data-testid={`ai-tool-${tool.key}`}>
      <div className="flex items-center gap-3 border-b-2 border-black px-5 py-4">
        <Icon size={20} className="text-primary" />
        <div>
          <div className="font-display uppercase text-lg leading-none">{tool.label}</div>
          <div className="font-mono text-xs opacity-70 mt-1">{tool.blurb}</div>
        </div>
      </div>
      <div className="p-5 space-y-3">
        {tool.select && (
          <select data-testid={`ai-kind-${tool.key}`} value={kind} onChange={(e) => setKind(e.target.value)}
            className="w-full border-2 border-black bg-background px-3 py-2 font-mono text-sm">
            {tool.select.map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
        )}
        {tool.placeholder !== "" && (
          <input data-testid={`ai-notes-${tool.key}`} value={notes} onChange={(e) => setNotes(e.target.value)}
            placeholder={tool.placeholder}
            className="w-full border-2 border-black bg-background px-3 py-2 font-mono text-sm" />
        )}
        <button data-testid={`ai-run-${tool.key}`} onClick={run} disabled={busy}
          className="inline-flex items-center gap-2 border-2 border-black bg-black text-background px-5 py-2.5 font-display uppercase shadow-hard btn-press disabled:opacity-50">
          <Sparkles size={16} /> {busy ? "Thinking…" : "Run"}
        </button>
        {out && (
          <pre data-testid={`ai-out-${tool.key}`}
            className="whitespace-pre-wrap border-2 border-black bg-accent/40 p-4 font-mono text-sm max-h-96 overflow-y-auto">{out}</pre>
        )}
      </div>
    </div>
  );
}

export function AIStudio() {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="ai-studio">
      <ImageLab />
      {TOOLS.map((t) => <ToolCard key={t.key} tool={t} />)}
    </div>
  );
}


function ImageLab() {
  const [prompt, setPrompt] = useState("");
  const [model, setModel] = useState("flash");
  const [refFile, setRefFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [images, setImages] = useState([]);
  const [error, setError] = useState("");

  const toB64 = (file) => new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });

  const run = async () => {
    setBusy(true);
    setError("");
    setImages([]);
    try {
      const body = { prompt, model };
      if (refFile) body.reference_b64 = await toB64(refFile);
      const res = await fetch(`${API}/api/admin/ai/image-generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("ut_admin_token")}` },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
      setImages(data.images || []);
    } catch (e) {
      setError(e.message);
    }
    setBusy(false);
  };

  return (
    <div className="border-2 border-black bg-background lg:col-span-2" data-testid="ai-tool-image-lab">
      <div className="flex items-center gap-3 border-b-2 border-black px-5 py-4">
        <ImageIcon size={20} className="text-primary" />
        <div>
          <div className="font-display uppercase text-lg leading-none">Image Lab</div>
          <div className="font-mono text-xs opacity-70 mt-1">Gemini Nano Banana image generation &amp; editing. Attach a reference photo to edit it.</div>
        </div>
      </div>
      <div className="p-5 space-y-3">
        <textarea data-testid="ai-image-prompt" value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={3}
          placeholder="Describe the image… e.g. 'product photo of a black heavyweight tee flat on light grey concrete, soft daylight'"
          className="w-full border-2 border-black bg-background px-3 py-2 font-mono text-sm" />
        <div className="flex flex-wrap items-center gap-3">
          <select data-testid="ai-image-model" value={model} onChange={(e) => setModel(e.target.value)}
            className="border-2 border-black bg-background px-3 py-2 font-mono text-sm">
            <option value="flash">Nano Banana Flash (fast)</option>
            <option value="pro">Nano Banana Pro (higher quality)</option>
          </select>
          <label className="inline-flex items-center gap-2 border-2 border-black bg-background px-3 py-2 font-mono text-sm cursor-pointer hover:bg-accent/40">
            <Paperclip size={14} />
            {refFile ? refFile.name : "Attach reference (optional)"}
            <input data-testid="ai-image-reference" type="file" accept="image/*" className="hidden"
              onChange={(e) => setRefFile(e.target.files[0] || null)} />
          </label>
          {refFile && (
            <button data-testid="ai-image-clear-ref" onClick={() => setRefFile(null)}
              className="font-mono text-xs underline">remove</button>
          )}
        </div>
        <button data-testid="ai-run-image-lab" onClick={run} disabled={busy || !prompt.trim()}
          className="inline-flex items-center gap-2 border-2 border-black bg-black text-background px-5 py-2.5 font-display uppercase shadow-hard btn-press disabled:opacity-50">
          <Sparkles size={16} /> {busy ? "Generating…" : "Generate"}
        </button>
        {error && <p data-testid="ai-image-error" className="font-mono text-sm text-red-700">{error}</p>}
        {images.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2" data-testid="ai-image-results">
            {images.map((u) => (
              <a key={u} href={`${API}${u}`} target="_blank" rel="noreferrer"
                className="block border-2 border-black shadow-hard hover:shadow-hard-lg transition-shadow">
                <img src={`${API}${u}`} alt="generated" className="w-full block" />
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
